// Read/write helpers for the `marketing` schema. Queries go directly to
// Supabase under RLS (auth.uid() = user_id) with the cookie session, so the
// browser only sees and writes the signed-in user's own rows. The schema must
// be in PostgREST's exposed list (supabase/config.toml [api].schemas /
// Management API per environment).
import { supabase } from '~/lib/supabase-browser';
import type { ActionMetric, ActionType, Brand, MarketingAction, Platform } from '~/types/marketing';

const mk = () => supabase.schema('marketing');

// PostgREST caps responses at db-max-rows; paginate so large sets don't
// silently truncate.
async function fetchAllRows<T>(buildQuery: () => any): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const { data, error } = await buildQuery().range(offset, offset + PAGE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    out.push(...(data as T[]));
    if (data.length < PAGE) break;
  }
  return out;
}

export async function loadBrands(userId: string): Promise<Brand[]> {
  return fetchAllRows<Brand>(() =>
    mk().from('brands').select('*').eq('user_id', userId).order('name')
  );
}

/** Actions whose scheduled_date falls in [fromISO, toISO] (inclusive, 'YYYY-MM-DD'). */
export async function loadActions(
  userId: string,
  fromISO: string,
  toISO: string
): Promise<MarketingAction[]> {
  return fetchAllRows<MarketingAction>(() =>
    mk()
      .from('actions')
      .select('*')
      .eq('user_id', userId)
      .gte('scheduled_date', fromISO)
      .lte('scheduled_date', toISO)
      .order('scheduled_date')
      .order('created_at')
  );
}

/** Everything the performance page needs: done actions + all metric snapshots. */
export async function loadPerformance(
  userId: string
): Promise<{ actions: MarketingAction[]; metrics: ActionMetric[] }> {
  const [actions, metrics] = await Promise.all([
    fetchAllRows<MarketingAction>(() =>
      mk()
        .from('actions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'done')
        .order('completed_at', { ascending: false })
    ),
    fetchAllRows<ActionMetric>(() =>
      mk().from('action_metrics').select('*').eq('user_id', userId).order('captured_at')
    ),
  ]);
  return { actions, metrics };
}

export interface NewActionInput {
  brand_id: string;
  platform: Platform;
  action_type: ActionType;
  title: string;
  description?: string | null;
  scheduled_date: string; // 'YYYY-MM-DD'
}

/** Insert a new action. user_id and status come from DB defaults (auth.uid(), 'pending'). */
export async function createAction(input: NewActionInput): Promise<MarketingAction> {
  const { data, error } = await mk().from('actions').insert(input).select('*').single();
  if (error) throw error;
  return data as MarketingAction;
}

export const PLATFORMS: Platform[] = ['reddit', 'substack', 'instagram', 'x', 'google'];

export const ACTION_TYPES: ActionType[] = [
  'post',
  'reel',
  'story',
  'ad',
  'comment',
  'reply',
  'follow',
  'like',
  'dm',
];

/** Latest metric snapshot per action (metrics must be sorted by captured_at asc). */
export function latestMetricByAction(metrics: ActionMetric[]): Map<string, ActionMetric> {
  const latest = new Map<string, ActionMetric>();
  for (const m of metrics) latest.set(m.action_id, m); // later rows overwrite
  return latest;
}

export const PLATFORM_LABELS: Record<string, string> = {
  reddit: 'Reddit',
  substack: 'Substack',
  instagram: 'Instagram',
  x: 'X',
  google: 'Google',
};

// Static class strings (light default + dark: variants) so Tailwind's scanner
// picks them up.
export const PLATFORM_BADGE: Record<string, string> = {
  reddit:
    'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/60 dark:text-orange-300 dark:border-orange-900',
  substack:
    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-900',
  instagram:
    'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-950/60 dark:text-fuchsia-300 dark:border-fuchsia-900',
  x: 'bg-neutral-100 text-neutral-700 border-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
  google:
    'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/60 dark:text-sky-300 dark:border-sky-900',
};

export const STATUS_PILL: Record<string, string> = {
  done: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/60 dark:text-green-400 dark:border-green-900',
  pending:
    'bg-neutral-100 text-neutral-500 border-neutral-300 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700',
  skipped:
    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-900',
};
